import { Box, Typography } from '@mui/material';
import { green, grey, red } from '@mui/material/colors';
import { format, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { Map } from 'immutable';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { fixedDecimals, prettifyParamNames, roundOrNull, scheduleFormatter, toHex } from '../utils';

import FieldEntry from './FieldEntry';

function SectionHeader({ children }) {
  return (
    <Typography
      variant="subtitle2"
      sx={{
        fontWeight: 700,
        fontSize: '0.85rem',
        pb: 0.5,
        mb: 1,
        mt: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      {children}
    </Typography>
  );
}

export default function StatusDetailVantage({ status, unit, isCurrent, unitTimeZone }) {
  const { t } = useTranslation();

  if (!status) return null;

  const majorVersion = status.get('major_version', 0);
  const minorVersion = status.get('minor_version', 0);
  const patchVersion = status.get('patch_version', 0);
  const fullVersion = `${majorVersion}.${minorVersion}.${patchVersion}`;

  return (
    <Box>
      <FieldEntry
        title={
          isCurrent
            ? t('statusView.detail.statusId', 'Status ID')
            : t('statusView.detail.historicalStatusId', 'Historical Status ID')
        }
        value={status.get('id')}
      />
      <FieldEntry
        title={t('statusView.detail.lastReceiveTimeYourTz', 'Last Receive Time (Your TZ)')}
        value={format(parseISO(status.get('last_receive_time')), 'Pp')}
        show={status.has('last_receive_time')}
      />
      <FieldEntry
        title={t('statusView.detail.lastReceiveTimeUnitTz', 'Last Receive Time (Unit TZ)')}
        value={format(toZonedTime(parseISO(status.get('last_receive_time')), unitTimeZone), 'Pp')}
        show={status.has('last_receive_time')}
      />
      <FieldEntry
        title={t('statusView.detail.boardType', 'Board Type')}
        value={toHex(status.get('board_type'))}
        show={status.has('board_type')}
      />
      <FieldEntry title={t('statusView.detail.version', 'Version')} value={fullVersion} />
      <FieldEntry
        title={t('statusView.detail.features', 'Features')}
        value={status.get('features', null) != null ? status.get('features', []).join(', ') : null}
      />
      <FieldEntry
        title={t('statusView.detail.signalStrength', 'Signal Strength')}
        value={status.get('received_signal_strength')}
        show={status.has('received_signal_strength')}
      />
      <FieldEntry
        title={t('statusView.detail.cpuTemp', 'CPU Temp (°C)')}
        value={roundOrNull(status.get('cpu_temperature', null), 1)}
      />
      <FieldEntry
        title={t('statusView.detail.sensorTemp', 'Sensor Temp (°C)')}
        value={roundOrNull(status.get('temperature', null), 1)}
      />

      <VantageParameters status={status} />
      <DeviceAlarms status={status} />
      <SensorReports status={status} />
      <RelayMonitor status={status} />
      <RelayReports status={status} />
      <RelaySchedules status={status} unit={unit} />
      <NetworkParameters status={status} />
    </Box>
  );
}

function VantageParameters({ status }) {
  const { t } = useTranslation();
  if (!status) return null;
  const vantageParams = status.get('vantage_parameters', {});
  if (!vantageParams) return null;

  const sensorReports = status.get('sensor_reports', []);
  const antRaw = sensorReports[1].sensor_parameters.color_thresholds[0].threshold;
  const ant1 = antRaw & 0x000000ff;
  const ant2 = (antRaw & 0x0000ff00) >> 8;
  const ant3 = (antRaw & 0x00ff0000) >> 16;

  const hasSensor1 = status.get('sensor_port_1', 0) != 0;
  const hasSensor2 = status.get('sensor_port_2', 0) != 0;
  const hasVantageSensors = hasSensor1 || hasSensor2;
  const port1HasHumidity = status.get('sensor_type_1', 0) == 1;
  const port2HasHumidity = status.get('sensor_type_2', 0) == 1;

  const temperatureSensorParams = status.get('temperature_sensor_parameters', {});
  const humiditySensorParams = status.get('humidity_sensor_parameters', {});
  let sensor1Temp = status.get('sensor_1_temp', [0, 0]);
  let sensor2Temp = status.get('sensor_2_temp', [0, 0]);
  if (!Array.isArray(sensor1Temp)) sensor1Temp = [0, 0];
  if (!Array.isArray(sensor2Temp)) sensor2Temp = [0, 0];
  let sensor1Humidity = status.get('sensor_1_humidity', 0) ?? 0.0;
  let sensor2Humidity = status.get('sensor_2_humidity', 0) ?? 0.0;

  return (
    <Box sx={{ mb: 1 }}>
      <SectionHeader>{t('statusView.vantage.vantage', 'Vantage')}</SectionHeader>
      <FieldEntry
        title={t('statusView.vantage.maxDataSession', 'Max Data Session')}
        value={vantageParams.max_data_session}
      />
      <FieldEntry
        title={t('statusView.vantage.checkInMinutesAfterMidnight', 'Check In (minutes after midnight)')}
        value={vantageParams.check_in_local_time}
      />
      <Box sx={{ my: 1 }} />
      <FieldEntry title={t('statusView.vantage.locationX', 'Location X')} value={sensorReports[0].location.x} />
      <FieldEntry title={t('statusView.vantage.locationY', 'Location Y')} value={sensorReports[0].location.y} />
      <FieldEntry title={t('statusView.vantage.size', 'Size')} value={sensorReports[0].location.size} />
      <FieldEntry title={t('statusView.vantage.gain', 'Gain')} value={sensorReports[0].sensor_parameters.gain} />
      <FieldEntry title={t('statusView.vantage.rate', 'Rate')} value={sensorReports[0].sensor_parameters.rate} />
      <FieldEntry
        title={t('statusView.vantage.resolution', 'Resolution')}
        value={sensorReports[0].sensor_parameters.resolution}
      />
      <FieldEntry
        title={t('statusView.vantage.stuckMargin', 'Stuck Margin')}
        value={sensorReports[0].sensor_parameters.color_thresholds[0].threshold}
      />
      <FieldEntry
        title={t('statusView.vantage.sensorThreshold', 'Sensor Threshold')}
        value={sensorReports[0].sensor_parameters.color_thresholds[1].threshold}
      />
      <FieldEntry
        title={t('statusView.vantage.stuckCount', 'Stuck Count')}
        value={sensorReports[0].sensor_parameters.color_thresholds[2].threshold}
      />
      <FieldEntry title={t('statusView.vantage.antCheckTime', 'Ant. Check Time')} value={ant1} />
      <FieldEntry title={t('statusView.vantage.antCount', 'Ant. Count')} value={ant2} />
      <FieldEntry title={t('statusView.vantage.antLevel', 'Ant. Level')} value={ant3} />

      {hasVantageSensors && (
        <Fragment>
          <Box sx={{ my: 1 }} />
          <FieldEntry title={t('statusView.vantage.port1', 'Port 1')} value={status.get('sensor_port_1', null)} />
          <FieldEntry
            title={t('statusView.vantage.port1Type', 'Port 1 Type')}
            value={status.get('sensor_type_1', null)}
          />
          <FieldEntry title={t('statusView.vantage.port2', 'Port 2')} value={status.get('sensor_port_2', null)} />
          <FieldEntry
            title={t('statusView.vantage.port2Type', 'Port 2 Type')}
            value={status.get('sensor_type_2', null)}
          />
          <Box sx={{ my: 1 }} />
          <Typography variant="subtitle2" sx={{ ml: 3, fontWeight: 600, fontSize: '0.8rem' }}>
            {t('statusView.vantage.temperatureSensors', 'Temperature Sensor(s)')}
          </Typography>
          <FieldEntry
            title={t('statusView.vantage.thresholdHigh', 'Threshold (High)')}
            value={temperatureSensorParams?.threshold?.high ?? '-'}
          />
          <FieldEntry
            title={t('statusView.vantage.thresholdLow', 'Threshold (Low)')}
            value={temperatureSensorParams?.threshold?.low ?? '-'}
          />
          <FieldEntry
            title={t('statusView.vantage.hysteresisC', 'Hysteresis (°C)')}
            value={temperatureSensorParams?.hysteresis ?? '-'}
          />
          {hasSensor1 && (
            <FieldEntry title={t('statusView.vantage.sensor1C', 'Sensor 1 (°C)')} value={sensor1Temp[0].toFixed(1)} />
          )}
          {hasSensor2 && (
            <FieldEntry title={t('statusView.vantage.sensor2C', 'Sensor 2 (°C)')} value={sensor2Temp[0].toFixed(1)} />
          )}
          {(port1HasHumidity || port2HasHumidity) && (
            <Fragment>
              <Box sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ ml: 3, fontWeight: 600, fontSize: '0.8rem' }}>
                {t('statusView.vantage.humiditySensors', 'Humidity Sensor(s)')}
              </Typography>
              <FieldEntry
                title={t('statusView.vantage.thresholdHigh', 'Threshold (High)')}
                value={humiditySensorParams?.threshold?.high ?? '-'}
              />
              <FieldEntry
                title={t('statusView.vantage.thresholdLow', 'Threshold (Low)')}
                value={humiditySensorParams?.threshold?.low ?? '-'}
              />
              <FieldEntry
                title={t('statusView.vantage.hysteresisPercent', 'Hysteresis (%)')}
                value={humiditySensorParams?.hysteresis}
              />
              {port1HasHumidity && (
                <FieldEntry
                  title={t('statusView.vantage.sensor1Percent', 'Sensor 1 (%)')}
                  value={sensor1Humidity.toFixed(1)}
                />
              )}
              {port2HasHumidity && (
                <FieldEntry
                  title={t('statusView.vantage.sensor2Percent', 'Sensor 2 (%)')}
                  value={sensor2Humidity.toFixed(1)}
                />
              )}
            </Fragment>
          )}
        </Fragment>
      )}
    </Box>
  );
}

function DeviceAlarms({ status }) {
  const { t } = useTranslation();
  const alarmStates = Map(status.get('alarm_states', {}));
  const deviceAlarms = Map(status.get('device_alarms_enabled', {}));

  const sorted = deviceAlarms
    .map((value, key) => ({ key, value }))
    .toList()
    .toJS()
    .sort((a, b) => a.key.localeCompare(b.key));

  return (
    <Box sx={{ mb: 1 }}>
      <SectionHeader>{t('statusView.detail.deviceAlarms', 'Device Alarms')}</SectionHeader>
      {sorted.map(obj => {
        const isAlarmActive = alarmStates.get(obj.key, false);
        return (
          <Box key={obj.key} sx={{ display: 'flex', justifyContent: 'space-between', maxWidth: 400, px: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
              {prettifyParamNames(obj.key)}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                px: 1,
                fontSize: '0.8rem',
                color: obj.value ? (isAlarmActive ? green[500] : red[500]) : grey[500],
              }}
            >
              {isAlarmActive ? t('general.active', 'Active') : t('general.inactive', 'Inactive')}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function RelayMonitor({ status }) {
  const { t } = useTranslation();
  const relayMonitor = Map(status.get('relay_monitor', {}))
    .map((value, key) => ({ key, value }))
    .toList();

  return (
    <Box sx={{ mb: 1 }}>
      <SectionHeader>{t('statusView.vantage.relayMonitor', 'Relay Monitor')}</SectionHeader>
      {relayMonitor.map(obj => (
        <FieldEntry
          key={obj.key}
          title={prettifyParamNames(obj.key)}
          value={obj.value ? t('general.enabled', 'Enabled') : t('general.disabled', 'Disabled')}
        />
      ))}
    </Box>
  );
}

function RelayReports({ status }) {
  const { t } = useTranslation();
  const relayReports = status.get('relay_reports', []);

  return (
    <Box sx={{ mb: 1 }}>
      <SectionHeader>{t('statusView.vantage.relayReports', 'Relay Reports')}</SectionHeader>
      {relayReports &&
        relayReports.map(report => {
          const measurements = report.measurements || {};
          return (
            <Box key={report.relay_number} sx={{ mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, px: 0.5, fontSize: '0.8rem' }}>
                {t('statusView.vantage.relayMeasurements', 'Relay {{number}} Measurements', {
                  number: report.relay_number,
                })}
              </Typography>
              <Typography variant="body2" sx={{ px: 1, fontSize: '0.8rem' }}>
                <strong>{t('statusView.vantage.current', 'Current')}</strong> {fixedDecimals(measurements.current, 2)}{' '}
                {t('statusView.vantage.maxValue', '(Max: {{value}})', {
                  value: fixedDecimals(measurements.max_current, 2),
                })}
              </Typography>
              <Typography variant="body2" sx={{ px: 1, fontSize: '0.8rem' }}>
                <strong>{t('statusView.vantage.voltage', 'Voltage')}</strong> {fixedDecimals(measurements.power, 2)}{' '}
                {t('statusView.vantage.maxValue', '(Max: {{value}})', {
                  value: fixedDecimals(measurements.max_power, 2),
                })}
              </Typography>
            </Box>
          );
        })}
    </Box>
  );
}

function RelaySchedules({ status, unit }) {
  const { t } = useTranslation();
  const fw = unit?.firmware_version ?? null;
  const schedulesPerRelay = fw?.schedules_per_relay ?? 2;
  const dayOfWeekScheduling = fw?.supports_day_of_week_schedules ?? false;
  const numberOfRelays = unit?.number_of_relays ?? 2;

  const schedules = [];
  for (let i = 1; i <= numberOfRelays; i++) {
    for (let j = 1; j <= schedulesPerRelay; j++) {
      schedules.push(
        <FieldEntry
          key={i + '_' + j}
          title={t('statusView.detail.relaySchedule', 'Relay {{relay}} Schedule {{schedule}}', {
            relay: i,
            schedule: j,
          })}
          value={scheduleFormatter(status.get(`relay_${i}_schedule_${j}`), dayOfWeekScheduling, t)}
        />,
      );
    }
  }

  return (
    <Box sx={{ mb: 1 }}>
      <SectionHeader>{t('statusView.detail.relaySchedules', 'Relay Schedules')}</SectionHeader>
      {schedules}
    </Box>
  );
}

function SensorReports({ status }) {
  const { t } = useTranslation();
  const sensorReports = status.get('sensor_reports', []);
  const sensorMeasurements = sensorReports[0]?.sensor_measurements;

  return (
    <Box sx={{ mb: 1 }}>
      <SectionHeader>{t('statusView.vantage.sensorReports', 'Sensor Reports')}</SectionHeader>
      {sensorMeasurements && (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, px: 0.5, mb: 0.5, fontSize: '0.8rem' }}>
            {t('statusView.vantage.lastRgbValue', 'Last RGB Value:')}
          </Typography>
          <FieldEntry title={t('statusView.vantage.red', 'Red')} value={sensorMeasurements.red.red} />
          <FieldEntry title={t('statusView.vantage.green', 'Green')} value={sensorMeasurements.green.red} />
          <FieldEntry title={t('statusView.vantage.blue', 'Blue')} value={sensorMeasurements.blue.red} />

          <Typography variant="body2" sx={{ fontWeight: 600, px: 0.5, mt: 1, mb: 0.5, fontSize: '0.8rem' }}>
            {t('statusView.vantage.alertRgbValue', 'Alert RGB Value:')}
          </Typography>
          <FieldEntry title={t('statusView.vantage.red', 'Red')} value={sensorMeasurements.red.green} />
          <FieldEntry title={t('statusView.vantage.green', 'Green')} value={sensorMeasurements.green.green} />
          <FieldEntry title={t('statusView.vantage.blue', 'Blue')} value={sensorMeasurements.blue.green} />

          <Typography variant="body2" sx={{ fontWeight: 600, px: 0.5, mt: 1, mb: 0.5, fontSize: '0.8rem' }}>
            {t('statusView.vantage.baselineRgbValue', 'Baseline RGB Value:')}
          </Typography>
          <FieldEntry title={t('statusView.vantage.red', 'Red')} value={sensorMeasurements.neutral.red} />
          <FieldEntry title={t('statusView.vantage.green', 'Green')} value={sensorMeasurements.neutral.green} />
          <FieldEntry title={t('statusView.vantage.blue', 'Blue')} value={sensorMeasurements.neutral.blue} />
        </Box>
      )}
    </Box>
  );
}

function NetworkParameters({ status }) {
  const { t } = useTranslation();
  const networkParameters = status.get('network_parameters', {});
  if (!networkParameters) return null;

  return (
    <Box sx={{ mb: 1 }}>
      <SectionHeader>{t('statusView.vantage.networkParameters', 'Network Parameters')}</SectionHeader>
      <FieldEntry title={t('statusView.detail.ipAddress', 'IP Address')} value={networkParameters.ip_address} />
      <FieldEntry title={t('statusView.vantage.gateway', 'Gateway')} value={networkParameters.gateway} />
      <FieldEntry title={t('statusView.vantage.serverIp', 'Server IP')} value={networkParameters.server_ip} />
      <FieldEntry title={t('statusView.vantage.serverPort', 'Server Port')} value={networkParameters.server_port} />
      <FieldEntry title={t('statusView.vantage.netmask', 'Netmask')} value={networkParameters.netmask} />
      <FieldEntry title={t('statusView.vantage.networkMode', 'Network Mode')} value={networkParameters.network_mode} />
    </Box>
  );
}
